import { OrderingUtils } from 'polygon-ordering';

const { dollarsToCents } = OrderingUtils;

/**
 * The top-up constraints for a single card (member money or gift card),
 * all amounts in cents. Used to validate an entered top-up amount before
 * it's submitted for payment.
 */
export interface TopUpLimits {
  minTopupCents: number;
  maxTopupCents: number;
  minMoneyBalanceCents: number;
  maxMoneyBalanceCents: number;
  moneyCents: number;
}

/**
 * The lowest amount that can be topped up onto this card, in cents.
 * Whichever is stricter: the card's flat minimum deposit, or however much is
 * needed to bring the balance up to the card's minimum balance.
 */
export const effectiveMinTopUpCents = (limits: TopUpLimits) =>
  Math.max(limits.minTopupCents, limits.minMoneyBalanceCents - limits.moneyCents);

/**
 * The highest amount that can be topped up onto this card, in cents.
 * Whichever is stricter: the card's flat maximum deposit, or however much
 * headroom remains before the balance hits the card's maximum balance.
 * Floored at 0 in case the card is already at or over its maximum balance.
 */
export const effectiveMaxTopUpCents = (limits: TopUpLimits) =>
  Math.max(0, Math.min(limits.maxTopupCents, limits.maxMoneyBalanceCents - limits.moneyCents));

/**
 * Formats a cents amount as a currency string using the given symbol, e.g. formatCents(1050, '$') -> '$10.50'.
 */
export const formatCents = (cents: number, currencySymbol: string) =>
  `${currencySymbol}${(cents / 100).toFixed(2)}`;

/**
 * Whether an entered dollar amount meets the card's minimum top-up requirement.
 * Returns true (no violation) when there is no effective minimum to enforce.
 */

export const isAboveMinTopUp = (value: number, limits: TopUpLimits): boolean => {
  const minCents = effectiveMinTopUpCents(limits);
  return !minCents || dollarsToCents(value) >= minCents;
};

/**
 * Whether an entered dollar amount is within the card's maximum top-up limit.
 */
export const isWithinMaxTopUp = (value: number, limits: TopUpLimits): boolean =>
  dollarsToCents(value) <= effectiveMaxTopUpCents(limits);

/**
 * cardsetHasTopUpLimits
 *
 * Checks to see if there are top up limits for the cardset
 * @param cardset: Cardset
 * @returns boolean
 */
export const cardsetHasTopUpLimits = (cardset: Cardset): boolean =>
  [cardset.minTopup, cardset.maxTopup, cardset.minMoneyBalance, cardset.maxMoneyBalance].every(
    v => v != null,
  );
